//src/components/ResetPassword.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../axiosConfig";
import "../styles/ResetPassword.css";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => String(params.get("token") || "").trim(), [params]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [tokenChecking, setTokenChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const passwordRef = useRef(null);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function verifyToken() {
      if (!token) {
        if (!cancelled) {
          setError("Link de redefinição inválido ou em falta.");
          setTokenValid(false);
          setTokenChecking(false);
        }
        return;
      }

      try {
        await api.post("/auth/verify-reset-token", { token });

        if (!cancelled) {
          setTokenValid(true);
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setTokenValid(false);
          setError(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              "Este link de redefinição é inválido ou já expirou."
          );
        }
      } finally {
        if (!cancelled) {
          setTokenChecking(false);
        }
      }
    }

    verifyToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  function onKeyEvent(e) {
    const caps = e.getModifierState?.("CapsLock");
    if (typeof caps === "boolean") setCapsOn(caps);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;

    setError("");

    if (!token) {
      setError("Link de redefinição inválido ou em falta.");
      return;
    }

    if (!tokenValid) {
      setError("Este link de redefinição é inválido ou já expirou.");
      return;
    }

    if (password !== confirmPassword) {
      setError("As palavras-passe não coincidem.");
      return;
    }

    setBusy(true);

    try {
      await api.post("/auth/reset-password", {
        token,
        password,
      });

      setDone(true);

      setTimeout(() => {
        navigate("/", { replace: true });
      }, 1200);
    } catch (err) {
      console.error("[reset-password] failed", err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Não foi possível redefinir a palavra-passe."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="reset-shell">
      <div className="reset-bg" aria-hidden="true">
        <div className="reset-bg-grid" />
        <div className="reset-bg-glow reset-bg-glow--a" />
        <div className="reset-bg-glow reset-bg-glow--b" />
      </div>

      <div className="reset-card">
        <header className="reset-header">
          <img
            src="/loadevo-logo.png"
            alt="LOADEvo"
            className="reset-brand-logo"
          />
          <h1 className="reset-title">Definir uma nova palavra-passe</h1>
          <p className="reset-subtitle">
            Escolha uma palavra-passe forte para a sua conta.
          </p>
        </header>

        {tokenChecking ? (
          <div className="reset-success" role="status">
            <div className="reset-success-badge">A VALIDAR</div>
            <p className="reset-success-text">
              A verificar a validade do link...
            </p>
          </div>
        ) : done ? (
          <div className="reset-success" role="status">
            <div className="reset-success-badge">PALAVRA-PASSE ATUALIZADA</div>
            <p className="reset-success-text">
              Palavra-passe atualizada com sucesso.
            </p>
            <p className="reset-success-note">
              A redirecionar para o início de sessão...
            </p>
          </div>
        ) : tokenValid ? (
          <form
            className="reset-form"
            onSubmit={handleSubmit}
            onKeyUp={onKeyEvent}
            onKeyDown={onKeyEvent}
          >
            {error && (
              <div className="reset-alert" role="alert">
                <svg viewBox="0 0 24 24" className="reset-alert-icon" aria-hidden="true">
                  <path
                    d="M12 9v4m0 4h.01M10.29 3.86l-8.4 14.54A1.5 1.5 0 003.2 21h17.6a1.5 1.5 0 001.31-2.6L13.7 3.86a1.5 1.5 0 00-2.6 0z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <label className="reset-field">
              <span className="reset-label">Nova palavra-passe</span>
              <div className={`reset-input-wrap ${error ? "is-invalid" : ""}`}>
                <input
                  ref={passwordRef}
                  className="reset-input reset-input-pw"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={onKeyEvent}
                  onKeyDown={onKeyEvent}
                  disabled={busy}
                  placeholder="••••••••"
                  required
                  aria-invalid={!!error}
                />
                <button
                  type="button"
                  className="reset-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  disabled={busy}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M17.94 17.94A10.94 10.94 0 0112 20C7 20 2.73 16.11 1 12c.46-1.06 1.08-2.06 1.82-2.94M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88M7.11 7.11A10.94 10.94 0 0112 4c5 0 9.27 3.89 11 8-.6 1.4-1.5 2.7-2.6 3.82M3 3l18 18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </label>

            <label className="reset-field">
              <span className="reset-label">Confirmar palavra-passe</span>
              <div className={`reset-input-wrap ${error ? "is-invalid" : ""}`}>
                <input
                  className="reset-input reset-input-pw"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyUp={onKeyEvent}
                  onKeyDown={onKeyEvent}
                  disabled={busy}
                  placeholder="••••••••"
                  required
                  aria-invalid={!!error}
                />
                <button
                  type="button"
                  className="reset-eye"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  disabled={busy}
                >
                  {showConfirmPassword ? (
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M17.94 17.94A10.94 10.94 0 0112 20C7 20 2.73 16.11 1 12c.46-1.06 1.08-2.06 1.82-2.94M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88M7.11 7.11A10.94 10.94 0 0112 4c5 0 9.27 3.89 11 8-.6 1.4-1.5 2.7-2.6 3.82M3 3l18 18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {capsOn && <div className="reset-hint">O Caps Lock está ativado</div>}
            </label>

            <button type="submit" className="reset-btn" disabled={busy}>
              <span className="reset-btn-text">
                {busy ? (
                  <>
                    <span className="reset-spinner" aria-hidden="true" />
                    A atualizar...
                  </>
                ) : (
                  "Atualizar palavra-passe"
                )}
              </span>
            </button>
          </form>
        ) : (
          <div className="reset-success" role="alert">
            <div className="reset-success-badge">LINK INVÁLIDO</div>
            <p className="reset-success-text">
              Este link de redefinição é inválido, já expirou ou já foi utilizado.
            </p>
            <p className="reset-success-note">
              Peça um novo email para redefinir a sua palavra-passe.
            </p>
          </div>
        )}

        <div className="reset-footer">
          <Link className="reset-back" to="/">
            Voltar ao iniciar sessão
          </Link>
        </div>
      </div>
    </div>
  );
}